import Component from "@glimmer/component";
import { cached } from "@glimmer/tracking";
import type { TOC } from "@ember/component/template-only";
import type { WithBoundArgs } from "@glint/template";
import { t } from "ember-intl";
import { eq } from "ember-truth-helpers";
import mergeClasses from "glesys-controlpanel/modifiers/merge-classes";
import Dropdown from "glesys-controlpanel/components/dropdown";
import GroupLabel from "glesys-controlpanel/components/dropdown/group-label";
import LinkItem from "glesys-controlpanel/components/dropdown/link-item";

export type SortQuery = { sortBy: string; sortDir: "asc" | "desc" };

export type SortOption<TItem> = {
  label: string;
  query: SortQuery;
  compare: (a: TItem, b: TItem) => number;
};

export type SortOptionGroup<TItem> = {
  label: string;
  options: SortOption<TItem>[];
};

export type SortOptions<TItem> = readonly (SortOption<TItem> | SortOptionGroup<TItem>)[];

function isSameSortQuery(a: SortQuery, b: SortQuery): boolean {
  return a.sortBy === b.sortBy && a.sortDir === b.sortDir;
}

function isSortOptionGroup<T>(item: SortOption<T> | SortOptionGroup<T>): item is SortOptionGroup<T> {
  return "options" in item;
}

interface SortControlSignature {
  Args: {
    options: SortOptions<unknown>;
    selected: SortOption<unknown> | undefined;
  };
  Element: HTMLButtonElement;
}

const SortControl: TOC<SortControlSignature> = <template>
  <Dropdown
    class="px-2 py-1 text-sm rounded-sm border border-gray-200 bg-white"
    aria-label={{t "component.sortable.aria-label" item=@selected.label}}
    {{mergeClasses}}
    ...attributes
  >
    <:header>
      <span class="text-gray-500" aria-hidden="true" data-test-sortable-label>{{t "component.sortable.label"}}</span>
      {{#if @selected}}<span class="text-black ml-1">{{@selected.label}}</span>{{/if}}
    </:header>
    <:default>
      {{#each @options as |item|}}
        {{#if (isSortOptionGroup item)}}
          <div role="group" aria-label={{item.label}}>
            <GroupLabel>{{item.label}}</GroupLabel>
            {{#each item.options as |option|}}
              <LinkItem
                class={{if (eq option @selected) "bg-gray-100 text-black font-medium"}}
                aria-current={{if (eq option @selected) "true"}}
                @query={{option.query}}
              >
                {{option.label}}
              </LinkItem>
            {{/each}}
          </div>
        {{else}}
          <LinkItem
            class={{if (eq item @selected) "bg-gray-100 text-black font-medium"}}
            aria-current={{if (eq item @selected) "true"}}
            @query={{item.query}}
          >
            {{item.label}}
          </LinkItem>
        {{/if}}
      {{/each}}
    </:default>
  </Dropdown>
</template>;

export interface SortableSignature<TItem> {
  Args: {
    items: readonly TItem[];
    options: SortOptions<TItem>;
    value: SortQuery;
  };
  Blocks: {
    default: [sorted: readonly TItem[], Control: WithBoundArgs<typeof SortControl, "options" | "selected">];
  };
  Element: HTMLDivElement;
}

export default class Sortable<TItem> extends Component<SortableSignature<TItem>> {
  @cached
  get selected(): SortOption<TItem> | undefined {
    return this.args.options
      .flatMap((item) => (isSortOptionGroup(item) ? item.options : [item]))
      .find((option) => isSameSortQuery(option.query, this.args.value));
  }

  get compare(): SortOption<TItem>["compare"] | undefined {
    return this.selected?.compare;
  }

  @cached
  get sorted(): readonly TItem[] {
    if (!this.compare) {
      return this.args.items;
    }
    return this.args.items.toSorted(this.compare);
  }

  get controlOptions(): SortOptions<unknown> {
    return this.args.options as SortOptions<unknown>;
  }

  get controlSelected(): SortOption<unknown> | undefined {
    return this.selected as SortOption<unknown> | undefined;
  }

  <template>
    <div ...attributes>
      {{yield this.sorted (component SortControl options=this.controlOptions selected=this.controlSelected)}}
    </div>
  </template>
}
